/**
 * Push-to-talk binding.
 *
 * - Desktop (window.neosocialDesktop with pttSupported): the Tauri app's
 *   global keyboard hook drives press and release, so PTT works while a game
 *   has focus. The bridge also covers keys pressed in our own window, so no
 *   window handler is added then (docs/build/desktop-bridge.md).
 * - Web: the key works only while this window is focused. Losing focus
 *   releases it (no stuck open mic), and the UI says so.
 *
 * Keys are `KeyboardEvent.code` (layout-independent: any keyboard layout
 * still gives "KeyV"). Mouse side buttons are "Mouse3".."Mouse4".
 */
import type { NeosocialDesktopBridge } from '@neosocial/protocol';

/** The part of the desktop bridge push-to-talk needs. */
export type DesktopPttBridge = Pick<NeosocialDesktopBridge, 'onPtt' | 'setPttKey'>;

/**
 * The client stores mouse buttons as "Mouse<MouseEvent.button>"; the desktop
 * shell names them MouseMiddle / MouseBack / MouseForward.
 */
export function toDesktopPttCode(code: string): string {
  switch (code) {
    case 'Mouse1':
      return 'MouseMiddle';
    case 'Mouse3':
      return 'MouseBack';
    case 'Mouse4':
      return 'MouseForward';
    default:
      return code;
  }
}

/** The reverse of {@link toDesktopPttCode}. */
export function fromDesktopPttCode(code: string): string {
  switch (code) {
    case 'MouseMiddle':
      return 'Mouse1';
    case 'MouseBack':
      return 'Mouse3';
    case 'MouseForward':
      return 'Mouse4';
    default:
      return code;
  }
}

export type PttMode = 'desktop' | 'focused';

export interface PttBinding {
  mode: PttMode;
  dispose(): void;
}

export interface PttOptions {
  code: string;
  onDown(): void;
  onUp(): void;
  bridge?: DesktopPttBridge | undefined;
  target?: Window;
}

/** True when the key press belongs to a text field (typing must never talk). */
export function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type;
    return !['checkbox', 'radio', 'button', 'range', 'submit'].includes(type);
  }
  return el.isContentEditable === true;
}

export function bindPtt(o: PttOptions): PttBinding {
  let down = false;
  const press = () => {
    if (down) return;
    down = true;
    o.onDown();
  };
  const release = () => {
    if (!down) return;
    down = false;
    o.onUp();
  };

  if (o.bridge) {
    o.bridge.setPttKey(toDesktopPttCode(o.code)).catch((err: unknown) => {
      console.warn('push-to-talk key refused by the desktop app:', err);
    });
    const off = o.bridge.onPtt((state) => (state === 'down' ? press() : release()));
    return {
      mode: 'desktop',
      dispose() {
        off();
        release();
      },
    };
  }

  const w = o.target ?? window;
  const mouseButton = /^Mouse(\d)$/.exec(o.code);
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.code !== o.code || e.repeat || isTypingTarget(e.target)) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    press();
  };
  const onKeyUp = (e: KeyboardEvent) => {
    if (e.code === o.code) release();
  };
  // a side button bound to push-to-talk must not also go back or forward
  // (Chrome navigates on its release): live bug 2026-10-28, voice dropped
  const onPointerDown = (e: PointerEvent | MouseEvent) => {
    if (!mouseButton || e.button !== Number(mouseButton[1])) return;
    if (e.button !== 0 && e.button !== 2) e.preventDefault();
    press();
  };
  const onPointerUp = (e: PointerEvent | MouseEvent) => {
    if (!mouseButton || e.button !== Number(mouseButton[1])) return;
    if (e.button !== 0 && e.button !== 2) e.preventDefault();
    release();
  };
  const onBlur = () => release();
  const onVis = () => {
    if (document.visibilityState === 'hidden') release();
  };
  w.addEventListener('keydown', onKeyDown, true);
  w.addEventListener('keyup', onKeyUp, true);
  w.addEventListener('mousedown', onPointerDown, true);
  w.addEventListener('mouseup', onPointerUp, true);
  w.addEventListener('blur', onBlur);
  w.document?.addEventListener('visibilitychange', onVis);
  return {
    mode: 'focused',
    dispose() {
      w.removeEventListener('keydown', onKeyDown, true);
      w.removeEventListener('keyup', onKeyUp, true);
      w.removeEventListener('mousedown', onPointerDown, true);
      w.removeEventListener('mouseup', onPointerUp, true);
      w.removeEventListener('blur', onBlur);
      w.document?.removeEventListener('visibilitychange', onVis);
      release();
    },
  };
}

/**
 * Keeps the browser from going back or forward when mouse side button
 * `button` (3 back, 4 forward) is released next: Chrome navigates on the
 * mouseup unless it is cancelled. Used when the button is being bound.
 */
export function swallowSideButtonNavigation(button: number, target: Window = window): void {
  if (button !== 3 && button !== 4) return;
  const stop = (e: MouseEvent) => {
    if (e.button !== button) return;
    e.preventDefault();
    target.removeEventListener('mouseup', stop, true);
  };
  target.addEventListener('mouseup', stop, true);
}

/** A readable label for a key code ("KeyV" → "V", "Space" → "Space"). */
export function keyLabel(code: string | null | undefined): string {
  if (!code) return '';
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Numpad')) return 'Num ' + code.slice(6);
  if (/^Mouse\d$/.test(code)) return 'Mouse ' + (Number(code.slice(5)) + 1);
  const map: Record<string, string> = {
    Backquote: '`',
    CapsLock: 'Caps Lock',
    ShiftLeft: 'Left Shift',
    ShiftRight: 'Right Shift',
    ControlLeft: 'Left Ctrl',
    ControlRight: 'Right Ctrl',
    AltLeft: 'Left Alt',
    AltRight: 'Right Alt',
    Space: 'Space',
  };
  return map[code] ?? code;
}
